import { z } from 'zod';

import {
  ADDRESS_REGEX,
  EMAIL_REGEX,
  MAX_ADDRESS_ONE_CHARACTERS,
  MAX_ADDRESS_TWO_CHARACTERS,
  MAX_CITY_CHARACTERS,
  MAX_FIRST_NAME_CHARACTERS,
  MAX_GUARDIANS_CHARACTERS,
  MAX_LAST_NAME_CHARACTERS,
  MAX_REASON_FOR_REFERRAL_CHARACTERS,
  MAX_STATE_CHARACTERS,
  MINIMUM_YEAR,
  NAME_REGEX,
  REASON_FOR_REFERRAL_REGEX,
  StepTwoFormKeys,
  ZIP_REGEX,
} from './constants';
import { isUnder18YearsOld, isZipValidForState } from './utils';

type TranslationFunction = (key: string, values?: Record<string, string | number>) => string;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const stepTwoSchema = (t: TranslationFunction) =>
  z
    .object({
      [StepTwoFormKeys.firstName]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.FirstName') })
        .max(MAX_FIRST_NAME_CHARACTERS, {
          message: t('OAS.Booking.Errors.MaxCharacters', { number: MAX_FIRST_NAME_CHARACTERS }),
        })
        .regex(NAME_REGEX, { message: t('OAS.Booking.Errors.Format.Name') }),
      [StepTwoFormKeys.lastName]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.LastName') })
        .max(MAX_LAST_NAME_CHARACTERS, {
          message: t('OAS.Booking.Errors.MaxCharacters', { number: MAX_LAST_NAME_CHARACTERS }),
        })
        .regex(NAME_REGEX, { message: t('OAS.Booking.Errors.Format.Name') }),
      [StepTwoFormKeys.dateOfBirth]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.Format.DateOfBirth') })
        .refine(
          (val) => {
            const date = new Date(val);
            return !isNaN(date.getTime());
          },
          {
            message: t('OAS.Booking.Errors.Format.DateOfBirth'),
          }
        )
        .refine(
          (val) => {
            const date = new Date(val);
            const now = new Date();

            return date <= now;
          },
          {
            message: t('OAS.Booking.Errors.DateOfBirthMax'),
          }
        )
        .refine(
          (val) => {
            const year = parseInt(val.split('-')[0], 10);
            return year >= MINIMUM_YEAR;
          },
          {
            message: t('OAS.Booking.Errors.DateOfBirth'),
          }
        ),
      [StepTwoFormKeys.gender]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.Gender') }),
      [StepTwoFormKeys.email]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.Email') })
        .regex(EMAIL_REGEX, { message: t('OAS.Booking.Errors.Format.Email') }),
      [StepTwoFormKeys.phoneNumber]: z
        .string()
        .trim()
        .min(13, { message: t('OAS.Booking.Errors.Format.Phone') }),
      [StepTwoFormKeys.parentOrGuardian]: z.string().trim().optional(),
      // .transform((val) => (typeof val === 'string' ? val.trim() : undefined))
      // .transform((val) => (val === '' ? null : val)),
      [StepTwoFormKeys.relationshipOfGuardian]: z.string().trim().optional(),
      // .transform((val) => (typeof val === 'string' ? val.trim() : undefined)),
      [StepTwoFormKeys.reasonForReferral]: z
        .string()
        .trim()
        .max(MAX_REASON_FOR_REFERRAL_CHARACTERS, {
          message: t('OAS.Booking.Errors.MaxCharacters', {
            number: MAX_REASON_FOR_REFERRAL_CHARACTERS,
          }),
        })
        .optional()
        .refine((val) => !val || REASON_FOR_REFERRAL_REGEX.test(val), {
          message: t('OAS.Booking.Errors.Format.Name'),
        }),
      [StepTwoFormKeys.address1]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.Address') })
        .max(MAX_ADDRESS_ONE_CHARACTERS, {
          message: t('OAS.Booking.Errors.MaxCharacters', { number: MAX_ADDRESS_ONE_CHARACTERS }),
        })
        .regex(ADDRESS_REGEX, { message: t('OAS.Booking.Errors.Format.Address') }),
      [StepTwoFormKeys.address2]: z
        .string()
        .trim()
        .max(MAX_ADDRESS_TWO_CHARACTERS, {
          message: t('OAS.Booking.Errors.MaxCharacters', { number: MAX_ADDRESS_TWO_CHARACTERS }),
        })
        .optional()
        .refine((val) => !val || ADDRESS_REGEX.test(val), {
          message: t('OAS.Booking.Errors.Format.Address'),
        }),
      // Todo: check are these .transforms for
      // .transform((val) => (val === '' ? null : val)),
      [StepTwoFormKeys.city]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.City') })
        .max(MAX_CITY_CHARACTERS, {
          message: t('OAS.Booking.Errors.MaxCharacters', { number: MAX_CITY_CHARACTERS }),
        })
        .regex(NAME_REGEX, { message: t('OAS.Booking.Errors.Format.City') }),
      [StepTwoFormKeys.state]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.State') }),
      [StepTwoFormKeys.zip]: z
        .string()
        .trim()
        .min(1, { message: t('OAS.Booking.Errors.Zip') })
        .regex(ZIP_REGEX, { message: t('OAS.Booking.Errors.Format.Zip') })
        .max(MAX_STATE_CHARACTERS, {
          message: t('OAS.Booking.Errors.MaxCharacters', { number: MAX_STATE_CHARACTERS }),
        }),
    })
    .superRefine((data, ctx) => {
      // If zip code is outside selected state
      if (data.zip && data.state && !isZipValidForState(data.state, data.zip)) {
        ctx.addIssue({
          path: [StepTwoFormKeys.zip],
          code: z.ZodIssueCode.custom,
          message:
            t('OAS.Booking.Errors.ZipInvalidForState') ||
            'Zip code is not valid for the selected state',
        });
      }

      // If user is under 18, parent or guardian and relationship of guardian are required and must be valid
      const isUnder = isUnder18YearsOld(data.dateOfBirth);
      if (isUnder) {
        // Parent or guardian input
        if (!data.parentOrGuardian || data.parentOrGuardian.trim() === '') {
          ctx.addIssue({
            path: [StepTwoFormKeys.parentOrGuardian],
            code: z.ZodIssueCode.custom,
            message: t('OAS.Booking.Errors.ParentOrGuardian'),
          });
        }

        if (data?.parentOrGuardian && data?.parentOrGuardian?.length > MAX_GUARDIANS_CHARACTERS) {
          ctx.addIssue({
            path: [StepTwoFormKeys.parentOrGuardian],
            code: z.ZodIssueCode.custom,
            message: t('OAS.Booking.Errors.MaxCharacters', { number: MAX_GUARDIANS_CHARACTERS }),
          });
        }

        if (data?.parentOrGuardian && !NAME_REGEX.test(data?.parentOrGuardian)) {
          ctx.addIssue({
            path: [StepTwoFormKeys.parentOrGuardian],
            code: z.ZodIssueCode.custom,
            message: t('OAS.Booking.Errors.Format.Name'),
          });
        }

        //  Relationship of guardian input
        if (!data.relationshipOfGuardian || data.relationshipOfGuardian.trim() === '') {
          ctx.addIssue({
            path: [StepTwoFormKeys.relationshipOfGuardian],
            code: z.ZodIssueCode.custom,
            message: t('OAS.Booking.Errors.RelationshipOfGuardian'),
          });
        }

        if (
          data?.relationshipOfGuardian &&
          data?.relationshipOfGuardian?.length > MAX_GUARDIANS_CHARACTERS
        ) {
          ctx.addIssue({
            path: [StepTwoFormKeys.relationshipOfGuardian],
            code: z.ZodIssueCode.custom,
            message: t('OAS.Booking.Errors.MaxCharacters', { number: MAX_GUARDIANS_CHARACTERS }),
          });
        }

        if (data?.relationshipOfGuardian && !NAME_REGEX.test(data?.relationshipOfGuardian)) {
          ctx.addIssue({
            path: [StepTwoFormKeys.relationshipOfGuardian],
            code: z.ZodIssueCode.custom,
            message: t('OAS.Booking.Errors.Format.Name'),
          });
        }
      }
    });

export type StepTwoFormData = z.infer<ReturnType<typeof stepTwoSchema>>;
